/**
 * ConnectorOwnerPicker — CONN-OWNER.
 *
 * Explicit Owner-Org + Owner-Tenant selector mounted above the
 * TenantScopePicker on both Add Connector forms (generic + GEL Platform).
 * Lets the operator pick which (org, tenant) the new connector belongs
 * to — distinct from the cross-tenant SHARE story which is downstream.
 *
 * Why this exists:
 *   Before CONN-OWNER, the Add Connector flow assumed session.tenantId
 *   was always the right owner. That broke for:
 *     • Platform Admin (no natural tenant — session pins to the
 *       __platform__ sentinel; they need to explicitly pick which
 *       customer's org+tenant the resource belongs to).
 *     • Org Admin with grants on multiple sibling tenants (often wants
 *       to create a connector under a tenant other than their pinned
 *       default).
 *
 * Behavior by role (caller's session.role):
 *   • Tenant Admin   — picker is HIDDEN. Owner is auto-set to
 *                      session.orgId + session.tenantId. They have no
 *                      cross-tenant choice to make.
 *   • Org Admin      — Org dropdown auto-fills session.orgId; tenant
 *                      dropdown lists all tenants under that org. They
 *                      can pick any sibling tenant they have rights on.
 *   • Customer Admin
 *     / Super Admin  — Org dropdown lists every org in the customer's
 *                      scope; tenant dropdown filters by selected org.
 *   • Platform Admin — same as Customer Admin, scoped to the customer
 *                      they've switched into via the user-menu. If
 *                      they haven't switched in yet, the form surfaces
 *                      a hint to switch first (we do NOT show a
 *                      customer dropdown inside the form — operators
 *                      are expected to context-switch via the
 *                      established user-menu flow).
 *
 * Contract:
 *   value     — { ownerOrgId, ownerTenantId } current selection
 *   onChange  — emits the updated selection
 *   sessionRole, sessionOrgId, sessionTenantId — passed from caller's
 *               auth context for role-based defaults + visibility.
 *   readOnly  — disables interaction (form is submitting).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Building2, Layers, Lock, AlertCircle, Loader2 } from 'lucide-react';
import * as api from '../../services/api';

/* Roles that get the picker. Tenant Admin gets auto-fill + hidden picker. */
const ROLES_WITH_FULL_PICKER = new Set([
  'Org Admin', 'Super Admin', 'Platform Admin',
]);

export default function ConnectorOwnerPicker({
  value = {},
  onChange,
  sessionRole = null,
  sessionOrgId = null,
  sessionTenantId = null,
  readOnly = false,
}) {
  const [orgs, setOrgs] = useState(null);   /* null = loading */
  const [tenants, setTenants] = useState(null);
  const [orgsError, setOrgsError] = useState(null);
  const [tenantsError, setTenantsError] = useState(null);
  const requestIdRef = useRef(0);

  const ownerOrgId = value.ownerOrgId || null;
  const ownerTenantId = value.ownerTenantId || null;

  const showPicker = ROLES_WITH_FULL_PICKER.has(sessionRole);

  /* Auto-fill for Tenant Admin (no choice to make). */
  useEffect(() => {
    if (showPicker) return;
    if (ownerOrgId && ownerTenantId) return;
    if (typeof onChange !== 'function') return;
    if (sessionOrgId && sessionTenantId) {
      onChange({ ownerOrgId: sessionOrgId, ownerTenantId: sessionTenantId });
    }
  }, [showPicker, sessionOrgId, sessionTenantId, ownerOrgId, ownerTenantId, onChange]);

  /* Load orgs the caller can see. Platform Admin: all orgs (the BE list
   * endpoint already returns the cross-customer set for PA). Other admin
   * roles: same endpoint returns their accessible orgs. */
  useEffect(() => {
    if (!showPicker) return;
    const reqId = ++requestIdRef.current;
    setOrgs(null);
    setOrgsError(null);
    api.getOrganizations()
      .then((res) => {
        if (reqId !== requestIdRef.current) return;
        if (res && res.ok === false) {
          setOrgsError(res.error || 'Failed to load orgs');
          setOrgs([]); return;
        }
        const list = (res && 'data' in res) ? res.data : res;
        const arr = Array.isArray(list) ? list : [];
        arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setOrgs(arr);
        /* Pre-fill from session if the caller hasn't picked yet. */
        if (!ownerOrgId && sessionOrgId && arr.some((o) => o.id === sessionOrgId)) {
          onChange?.({ ownerOrgId: sessionOrgId, ownerTenantId: ownerTenantId || sessionTenantId || null });
        } else if (!ownerOrgId && arr.length === 1) {
          /* Exactly one accessible org → auto-select. */
          onChange?.({ ownerOrgId: arr[0].id, ownerTenantId: null });
        }
      })
      .catch((e) => {
        if (reqId !== requestIdRef.current) return;
        setOrgsError(e?.message || String(e));
        setOrgs([]);
      });
    /* Deps intentionally narrow: re-fetch on role change only. Session
     * ids are stable for a given role; ownerOrgId reads are pre-fill
     * side-effects — including them would re-fire on every selection. */
    // eslint-disable-next-line
  }, [showPicker, sessionRole]);

  /* Load tenants for the selected org. Empties when org changes. */
  useEffect(() => {
    if (!showPicker || !ownerOrgId) {
      setTenants(null);
      return;
    }
    const reqId = ++requestIdRef.current;
    setTenants(null);
    setTenantsError(null);
    if (typeof api.getTenantsByOrg !== 'function') {
      setTenants([]);
      return;
    }
    api.getTenantsByOrg(ownerOrgId)
      .then((res) => {
        if (reqId !== requestIdRef.current) return;
        if (res && res.ok === false) {
          setTenantsError(res.error || 'Failed to load tenants');
          setTenants([]); return;
        }
        const body = (res && 'data' in res) ? res.data : res;
        let list = null;
        if (Array.isArray(body)) list = body;
        else if (body && Array.isArray(body.items)) list = body.items;
        else if (body && Array.isArray(body.tenants)) list = body.tenants;
        else if (body && Array.isArray(body.content)) list = body.content;
        const arr = Array.isArray(list) ? list : [];
        arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setTenants(arr);
        /* Pre-fill tenant from session if the caller's session.tenantId
         * belongs to the chosen org and they haven't picked yet. */
        if (!ownerTenantId && sessionTenantId && arr.some((t) => t.id === sessionTenantId)) {
          onChange?.({ ownerOrgId, ownerTenantId: sessionTenantId });
        } else if (!ownerTenantId && arr.length === 1) {
          /* Exactly one tenant under the org → auto-select. */
          onChange?.({ ownerOrgId, ownerTenantId: arr[0].id });
        }
      })
      .catch((e) => {
        if (reqId !== requestIdRef.current) return;
        setTenantsError(e?.message || String(e));
        setTenants([]);
      });
    /* Deps narrow on purpose: re-fetching tenants on every ownerTenantId
     * change would loop with the pre-fill side effect inside the .then(). */
    // eslint-disable-next-line
  }, [ownerOrgId, showPicker]);

  /* Read-only mode (Tenant Admin) — render a compact summary instead. */
  if (!showPicker) {
    return (
      <div className="rounded-v2-md bg-v2-muted text-[11.5px] text-v2-ink-muted p-3 border border-v2-border-subtle">
        <div className="flex items-center gap-1.5 mb-1 text-v2-ink">
          <Lock size={11} aria-hidden="true" />
          <strong className="font-semibold">Owner</strong>
        </div>
        This connector will be owned by your tenant ({sessionTenantId
          ? <code className="font-mono-v2 text-[10.5px]">{shortId(sessionTenantId)}</code>
          : 'session tenant'}).{' '}
        To create connectors under other tenants you'd need Org Admin or higher.
      </div>
    );
  }

  const selectedOrgName = useMemo(() => {
    if (!Array.isArray(orgs)) return null;
    const o = orgs.find((x) => x.id === ownerOrgId);
    return o ? (o.name || o.id) : null;
  }, [orgs, ownerOrgId]);

  return (
    <div className="flex flex-col gap-3" aria-label="Connector owner scope">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold">
          Owner
        </span>
        <span className="text-[10.5px] text-v2-ink-muted">
          Which tenant will own this connector
        </span>
      </div>

      {/* Org selector */}
      <label className="block">
        <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold flex items-center gap-1">
          <Building2 size={11} aria-hidden="true" /> Org
          <span className="text-v2-danger-strong">*</span>
        </span>
        {orgs == null ? (
          <div className="mt-1 flex items-center gap-2 text-[11.5px] text-v2-ink-muted">
            <Loader2 size={11} className="animate-spin" aria-hidden="true" />
            Loading orgs…
          </div>
        ) : orgsError ? (
          <div className="mt-1 flex items-center gap-2 text-[11.5px] text-v2-danger-strong">
            <AlertCircle size={11} aria-hidden="true" /> {orgsError}
          </div>
        ) : (
          <select
            value={ownerOrgId || ''}
            onChange={(e) => onChange?.({ ownerOrgId: e.target.value || null, ownerTenantId: null })}
            disabled={readOnly || orgs.length === 0}
            className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2 py-1.5 text-[12.5px] text-v2-ink v2-focus-ring"
          >
            <option value="">— Select an org —</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name || o.id}
              </option>
            ))}
          </select>
        )}
        {orgs != null && orgs.length === 0 && !orgsError && (
          <div className="mt-1 text-[10.5px] text-v2-warning-strong">
            No accessible orgs in your scope. Platform Admin: switch into a customer
            via the user menu first. Other roles: contact your Org Admin to be granted on an org.
          </div>
        )}
      </label>

      {/* Tenant selector — disabled until an org is picked */}
      <label className="block">
        <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold flex items-center gap-1">
          <Layers size={11} aria-hidden="true" /> Tenant
          <span className="text-v2-danger-strong">*</span>
        </span>
        {!ownerOrgId ? (
          <div className="mt-1 text-[11.5px] text-v2-ink-muted italic">
            Pick an org above first.
          </div>
        ) : tenants == null ? (
          <div className="mt-1 flex items-center gap-2 text-[11.5px] text-v2-ink-muted">
            <Loader2 size={11} className="animate-spin" aria-hidden="true" />
            Loading tenants…
          </div>
        ) : tenantsError ? (
          <div className="mt-1 flex items-center gap-2 text-[11.5px] text-v2-danger-strong">
            <AlertCircle size={11} aria-hidden="true" /> {tenantsError}
          </div>
        ) : (
          <select
            value={ownerTenantId || ''}
            onChange={(e) => onChange?.({ ownerOrgId, ownerTenantId: e.target.value || null })}
            disabled={readOnly || tenants.length === 0}
            className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2 py-1.5 text-[12.5px] text-v2-ink v2-focus-ring"
          >
            <option value="">— Select a tenant —</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name || t.id}
              </option>
            ))}
          </select>
        )}
        {ownerOrgId && tenants != null && tenants.length === 0 && !tenantsError && (
          <div className="mt-1 text-[10.5px] text-v2-warning-strong">
            No tenants in <strong>{selectedOrgName || 'this org'}</strong>. An Org Admin must
            create at least one tenant before connectors can be created here.
          </div>
        )}
      </label>

      <div className="text-[10.5px] text-v2-ink-muted leading-snug">
        Only Admins of the selected tenant can edit credentials, rotate keys,
        or delete this connector after creation. To grant additional tenants
        read-and-use access, use the tenant scope picker below.
      </div>
    </div>
  );
}

/* Pretty short-form for a UUID — first 8 chars. */
function shortId(uuid) {
  if (!uuid) return '';
  const s = String(uuid);
  return s.length > 8 ? s.slice(0, 8) + '…' : s;
}
